import {
  DEFAULT_BETTER_THAN_NEXT_BEST_DOLLARS_THRESHOLD,
  DEFAULT_BETTER_THAN_NEXT_BEST_PERCENTAGE_THRESHOLD,
  DEFAULT_MIN_BUYABLE_PERCENTAGE,
  DEMINIMIS_EXCLUDED_COUNTRIES,
  DEMINIMIS_THRESHOLD,
  landedCostFeeDescriptions,
  PAYMENT_PROCESSING_FEE,
  WIRE_FEE,
  WIRE_FEE_THRESHOLD,
} from 'constants/procurementConstants';
import jsonLogic from 'json-logic-js';
import _ from 'lodash';
import {
  PackagingOptions,
  PurchaseRule,
  StockLocationLite,
} from 'types/inventory';
import {
  BaseBid,
  DecoratedOffer,
  DecoratedOfferGroup,
  FilteredLineOffers,
  LandedCostLine,
  ManagedByCofactrPreference,
  OfferLinesToUse,
  OffersToUse,
  OptimizeOffersRequest,
  OptimizePreferences,
  OptimizerBid,
  OptimizerLineBids,
  OptimizerResult,
  Supplier,
  TariffRate,
} from 'types/offerOptimizer';
import {
  Offer,
  OfferType,
  OfferWithPartId,
  Part,
  PartCore,
  PartOffer,
  PartPrice,
  PartSupply,
  PriceBreak,
  ReferencePrice,
  ReferencePriceLite,
} from 'types/part';
import { OrgPartSchema } from 'types/clientV2/parts';
import { PartRuleSet } from 'types/partRules';
import { CofactrPurchaseChargeType } from 'types/procurement';
import { PriceModel } from 'types/purchasing';
import { Price } from 'types/sourcing';
import {
  OrgSupplier,
  OrgSupplierConnection,
  PublicSupplier,
} from 'types/suppliers';
import { camelizeVars } from 'utils/functions';

export const calcReferenceBreakPrice = (
  referencePrices: ReferencePrice[] | ReferencePriceLite[],
  quant: number
) => {
  // normalize the reference price types
  const normalizedReferencePrices = _.map(referencePrices, (p) => ({
    quantity: p.quantity,
    price: 'price' in p ? _.toNumber(p.price) : _.toNumber(p.convertedPrice),
  }));

  if (!normalizedReferencePrices.length) return 0;
  const sortedPrices = _.chain(normalizedReferencePrices)
    .sortBy('quantity')
    .reverse()
    .value();

  let price: number | null = null;
  for (const p of sortedPrices) {
    if (quant >= p.quantity) {
      price = _.toNumber(p.price);
      break;
    }
  }
  if (price === null) {
    const first = _.first(sortedPrices);
    if (!first) return 0;
    price = _.toNumber(first.price);
  }

  if (!price) return 0;

  return price;
};

export const calcOfferBreakPrice = (
  prices: PartPrice[] | PriceBreak[],
  quant: number,
  bulkQuant?: number
) => {
  if (!prices.length) return 0;

  const calculatedBulkQuant = bulkQuant || 1;
  const sortedPrices = _.chain(prices)
    .sortBy('priceBreak')
    .reverse()
    .value() as PartPrice[] | PriceBreak[];

  let price: number | null = null;
  for (const p of sortedPrices) {
    if (quant >= p.priceBreak * calculatedBulkQuant) {
      price = _.toNumber(p.price) / calculatedBulkQuant;
      break;
    }
  }
  if (price === null) {
    const first = _.first(_.reverse(sortedPrices));
    if (!first) return 0;
    price = _.toNumber(first.price) / calculatedBulkQuant;
  }

  if (!price) return 0;

  return price;
};

export const calcSupplierQuoteBreakPrice = (prices: Price[], quant: number) => {
  if (!prices.length) return 0;
  const sortedPrices = _.orderBy(prices, ['from'], ['desc']);

  let price: number | null = null;
  for (const p of sortedPrices) {
    if (quant >= p.from) {
      price = _.toNumber(p.unitPrice);
      break;
    }
  }
  if (price === null) {
    const first = _.first(_.reverse(sortedPrices));
    if (!first) return 0;
    price = _.toNumber(first.unitPrice);
  }

  if (!price) return 0;

  return price;
};

export const calcQuantWithMoqAndMultiple = (
  quant: number,
  moq: number,
  multiple: number
) => {
  // apply moq/multiple
  const quantToBuy = Math.max(Math.ceil(quant / multiple) * multiple, moq);
  return quantToBuy;
};

export const calcBestOfferBreakPrice = (
  offer: PartOffer | Offer,
  quant: number,
  allowBackorder: boolean,
  useUnitOfMeasure?: boolean
) => {
  if (!offer.prices.length) return null;

  const convertedMoq = useUnitOfMeasure
    ? _.toNumber(offer.moq) * _.toNumber(offer.bulkQuant || 1)
    : _.toNumber(offer.moq);
  const convertedMultiple = useUnitOfMeasure
    ? _.toNumber(offer.multiple) * _.toNumber(offer.bulkQuant || 1)
    : _.toNumber(offer.multiple);
  const convertedReportedStock = useUnitOfMeasure
    ? _.toNumber(offer.reportedStock) * _.toNumber(offer.bulkQuant || 1)
    : _.toNumber(offer.reportedStock);
  const bulkQuant = useUnitOfMeasure ? _.toNumber(offer.bulkQuant || 1) : 1;

  // apply moq/multiple
  const quantToBuy = Math.max(
    Math.ceil(quant / convertedMultiple) * convertedMultiple,
    convertedMoq
  );

  // calculate total price for each priceBreak that is less than the offer available stock
  const bestPriceBreak = _.chain(offer.prices)
    .filter(
      (p) =>
        p.priceBreak * bulkQuant <= convertedReportedStock || allowBackorder
    )
    .map((p) => ({
      priceBreak: p.priceBreak * bulkQuant,
      unitPrice: p.price / bulkQuant,
      totalPrice:
        (p.price / bulkQuant) * Math.max(quantToBuy, p.priceBreak * bulkQuant),
      purchaseQuant: Math.max(quantToBuy, p.priceBreak),
    }))
    .sortBy('totalPrice')
    .first()
    .value();

  return bestPriceBreak;
};

export const calcOverage = (request: {
  part: Part | PartCore;
  supply?: PartSupply;
  quant: number;
  returnCalculationProps?: boolean;
}) => {
  let minimum = 0; // defines the fewest total number of allowable parts
  let minOverage = 0; // defines the fewest number of overage parts
  let percentOverage = 0; // defines overage percentage

  // calculate the reference price
  let price = calcReferenceBreakPrice(
    request.supply?.referencePrices ||
      (request?.part && 'referencePrices' in request.part
        ? request.part.referencePrices
        : []) ||
      [],
    request.quant
  );
  if (!price) {
    price = 4; // parts with no price data are usually not super cheap in practice so this is hardcoded to produce a generally correct result
  }

  // handle through hole parts
  if (_.toLower(request.part.terminationType || '') !== 'smt') {
    if (price < 1) {
      minimum = 2;
      minOverage = 2;
      percentOverage = 0.05;
    } else if (price >= 1 && price < 5) {
      minimum = 2;
      minOverage = 2;
      percentOverage = 0.02;
    } else {
      minimum = 2;
      minOverage = 1;
      percentOverage = 0.02;
    }
  }

  // handle passives based on footprint
  if (request.part.package) {
    if (_.includes(['01005', '0201', '0402'], request.part.package)) {
      minimum = 200;
      minOverage = 100;
      percentOverage = 0.5;
    } else if (_.includes(['0603', '0805'], request.part.package)) {
      minimum = 50;
      minOverage = 25;
      percentOverage = 0.1;
    } else if (
      _.includes(
        [
          '1008',
          '1206',
          '1210',
          '1806',
          '1812',
          '1825',
          '2010',
          '2512',
          '2725',
          '2920',
        ],
        request.part.package
      )
    ) {
      minimum = 20;
      minOverage = 10;
      percentOverage = 0.1;
    }
  }

  // handle everything else based on price
  if (!minimum) {
    if (price < 1) {
      minimum = 10;
      minOverage = 10;
      percentOverage = 0.15;
    } else if (price >= 1 && price < 5) {
      minimum = 5;
      minOverage = 5;
      percentOverage = 0.1;
    } else {
      minimum = 2;
      minOverage = 2;
      percentOverage = 0.05;
    }
  }

  if (request.returnCalculationProps) {
    return {
      minimum,
      minOverage,
      percentOverage,
    };
  }

  // apply overage calculation
  const overage = Math.max(request.quant * percentOverage, minOverage);
  const quant = Math.ceil(Math.max(request.quant + overage, minimum));
  return quant - request.quant;
};

export const applyOverage = (request: {
  minimum: number;
  minOverage: number;
  percentOverage: number;
  quant: number;
}) => {
  const overage = Math.max(
    request.quant * request.percentOverage,
    request.minOverage
  );
  const quant = Math.ceil(Math.max(request.quant + overage, request.minimum));
  return quant - request.quant;
};

export const calcOverageFromPartRuleSet = (request: {
  part: Part | PartCore;
  supply?: PartSupply;
  quant: number;
  partRuleSet?: PartRuleSet | null;
  returnMinimum?: boolean;
  returnCalculationProps?: boolean;
}) => {
  if (!request.partRuleSet) {
    return calcOverage(request);
  }

  let minimum = 0; // defines the fewest total number of allowable parts
  let minOverage = 0; // defines the fewest number of overage parts
  let percentOverage = 0; // defines overage percentage

  // calculate the reference price
  let price = calcReferenceBreakPrice(
    request.supply?.referencePrices ||
      (request?.part && 'referencePrices' in request.part
        ? request.part.referencePrices
        : []) ||
      [],
    request.quant
  );
  if (!price) {
    price = 4; // parts with no price data are usually not super cheap in practice so this is hardcoded to produce a generally correct result
  }

  const sortedRules = _.sortBy(request.partRuleSet.rules, 'evaluationOrder');

  const variables = { ...request.part, price };

  _.every(sortedRules, (rule) => {
    const ruleQuery = camelizeVars(JSON.parse(rule.query));
    if (jsonLogic.apply(ruleQuery, variables)) {
      minimum = request.returnMinimum
        ? rule.minimumAttritionMinTotal
        : rule.attritionMinTotal;
      minOverage = request.returnMinimum
        ? rule.minimumAttritionQuantAdditional
        : rule.attritionQuantAdditional;
      percentOverage = request.returnMinimum
        ? rule.minimumAttritionPercentAdditional
        : rule.attritionPercentAdditional;
      return false;
    }
    return true;
  });

  if (request.returnCalculationProps) {
    return {
      minimum,
      minOverage,
      percentOverage,
    };
  }

  // apply overage calculation
  const overage = Math.max(request.quant * percentOverage, minOverage);
  const quant = Math.ceil(Math.max(request.quant + overage, minimum));
  return quant - request.quant;
};

const calcSortedBids = (
  offers: (Offer | PartOffer | DecoratedOffer)[],
  quant: number,
  addReeling?: boolean,
  useUnitOfMeasure?: boolean
) =>
  _.chain(offers)
    .filter(
      (o) =>
        (!!o.supplier.buyable ||
          o.offerType === OfferType.QUOTE ||
          o.offerType === OfferType.SUPPLIER_QUOTE) &&
        o.reportedStock > 0 &&
        o.reportedStock >= _.toNumber(o.moq || 1) &&
        o.prices.length > 0
    )
    .map((o) => {
      const addReelingPrice =
        addReeling && o.customReelSku
          ? _.toNumber((o as DecoratedOffer).orgSupplier?.customReelPrice || 0)
          : 0;

      const calculatedBulkQuant = useUnitOfMeasure
        ? _.toNumber(o.bulkQuant || 1)
        : 1;
      const convertedReportedStock = useUnitOfMeasure
        ? _.toNumber(o.reportedStock) * calculatedBulkQuant
        : _.toNumber(o.reportedStock);
      const convertedMoq = useUnitOfMeasure
        ? _.toNumber(o.moq || 1) * _.toNumber(o.bulkQuant || 1)
        : _.toNumber(o.moq || 1);
      const convertedMultiple = useUnitOfMeasure
        ? _.toNumber(o.multiple || 1) * _.toNumber(o.bulkQuant || 1)
        : _.toNumber(o.multiple || 1);

      if (convertedReportedStock >= quant) {
        // handle moq/multiple
        const bidQuant =
          Math.min(
            Math.ceil(Math.max(convertedMoq, quant) / convertedMultiple),
            convertedReportedStock
          ) * convertedMultiple;

        // handle round to higher price break to save money
        const breakPrices = [
          {
            priceBreak: bidQuant,
            price:
              calcOfferBreakPrice(o.prices, bidQuant, calculatedBulkQuant) *
              bidQuant,
          },
          ..._.chain(o.prices)
            .filter((p) => p.priceBreak * calculatedBulkQuant > bidQuant)
            .map((p) => ({
              priceBreak: p.priceBreak * calculatedBulkQuant,
              price: (_.toNumber(p.price) * p.priceBreak) / calculatedBulkQuant,
            }))
            .value(),
        ];
        const bestPriceBreak = _.chain(breakPrices)
          .sortBy('price')
          .first()
          .value();

        return {
          bidQuant: bestPriceBreak.priceBreak,
          bidPrice: _.toNumber(bestPriceBreak.price) + addReelingPrice,
          unitPrice:
            (_.toNumber(bestPriceBreak.price) + addReelingPrice) /
            Math.min(bidQuant, quant),
          offer: o,
        };
      }

      const bidPrice =
        calcOfferBreakPrice(
          o.prices,
          convertedReportedStock,
          calculatedBulkQuant
        ) * convertedReportedStock;
      return {
        bidQuant: convertedReportedStock,
        bidPrice: bidPrice + addReelingPrice,
        unitPrice: (bidPrice + addReelingPrice) / convertedReportedStock,
        offer: o,
      };
    })
    .sortBy('unitPrice')
    .value();

export const calcBestUnitPrice = (request: {
  part?: Part | PartCore | OrgPartSchema;
  supply?: PartSupply;
  altsSupply?: PartSupply[];
  quant: number;
  ignoreReportedStock?: boolean;
}) => {
  // collapse alt offers
  const offers = _.compact([
    ...(request.supply?.offers || []),
    ..._.chain(request.altsSupply || [])
      .map('offers')
      .flatten()
      .value(),
  ]);

  // calculate using reference price if supply is not provided or no offers are in supply
  if (!request.supply || !offers.length) {
    const referencePrice = calcReferenceBreakPrice(
      request.supply?.referencePrices ||
        (request?.part && 'buyableReferencePrices' in request.part
          ? request.part.buyableReferencePrices
          : []) ||
        [],
      request.quant
    );

    if (!referencePrice) {
      return calcReferenceBreakPrice(
        request.supply?.referencePrices ||
          (request?.part && 'referencePrices' in request.part
            ? request.part.referencePrices
            : []) ||
          [],
        request.quant
      );
    }
    return referencePrice;
  }

  if (request.ignoreReportedStock) {
    const cheapestOffer = _.chain(offers)
      .compact()
      .filter(
        (o) =>
          (o.supplier.buyable ||
            o.offerType === OfferType.QUOTE ||
            o.offerType === OfferType.SUPPLIER_QUOTE) &&
          o.prices.length > 0
      )
      .sortBy((o) =>
        calcOfferBreakPrice(o.prices, request.quant, o.bulkQuant || 1)
      )
      .first()
      .value();

    if (cheapestOffer) {
      return calcOfferBreakPrice(
        cheapestOffer.prices,
        request.quant,
        cheapestOffer.bulkQuant || 1
      );
    }

    return 0;
  }

  // calculate using buyable offers
  const sortedBids = calcSortedBids(offers, request.quant, false, true);

  let quantToCover = request.quant; // this will track how much of the request quant we still need to price a source for
  let totalPrice = 0;
  const usedBids = []; // this will track the bids used to calculate the total price
  for (const bid of sortedBids) {
    if (quantToCover > 0) {
      if (bid.bidQuant <= quantToCover) {
        quantToCover -= bid.bidQuant;
        totalPrice += bid.bidPrice;
        usedBids.push(bid);
      } else {
        const rebidQuant = Math.max(quantToCover, bid.offer.moq);
        const rebidPrice =
          calcOfferBreakPrice(
            bid.offer.prices,
            rebidQuant,
            bid.offer.bulkQuant || 1
          ) * rebidQuant;
        totalPrice += rebidPrice;
        quantToCover = 0;
        usedBids.push(bid);
      }
    } else {
      break;
    }
  }
  if (!quantToCover) {
    // check if a single bid could have covered the required quantity at a lower price
    for (const bid of usedBids) {
      if (bid.bidQuant >= request.quant) {
        totalPrice = bid.bidPrice;
      }
    }

    return totalPrice / request.quant;
  }

  // fall back on using backorder offers
  const cheapestBackorderOffer = _.chain(offers)
    .compact()
    .filter(
      (o) =>
        (o.supplier.buyable ||
          o.offerType === OfferType.QUOTE ||
          o.offerType === OfferType.SUPPLIER_QUOTE) &&
        o.prices.length > 0 &&
        o.factoryLeadDays > 0
    )
    .sortBy((o) =>
      calcOfferBreakPrice(o.prices, request.quant, o.bulkQuant || 1)
    )
    .first()
    .value();

  if (cheapestBackorderOffer) {
    return calcOfferBreakPrice(
      cheapestBackorderOffer.prices,
      request.quant,
      cheapestBackorderOffer.bulkQuant || 1
    );
  }

  return 0;
};

export const calcMinLead = (request: {
  part?: Part | PartCore | OrgPartSchema;
  supply?: PartSupply;
  altsSupply?: PartSupply[];
  quant: number;
}) => {
  // filter and sort offers
  const sortedOffers = _.chain([
    ...(request.supply?.offers || []),
    ..._.chain(request.altsSupply || [])
      .map('offers')
      .flatten()
      .value(),
  ])
    .compact()
    .filter(
      (o) =>
        (o.supplier.buyable ||
          o.offerType === OfferType.QUOTE ||
          o.offerType === OfferType.SUPPLIER_QUOTE) &&
        o.reportedStock > 0 &&
        o.prices.length > 0
    )
    .sortBy('lead')
    .value();

  let quantToCover = request.quant; // this will track how much of the request quant we still need to cover
  let minLead = 0;
  for (const offer of sortedOffers) {
    if (quantToCover > 0) {
      minLead = offer.lead;
      quantToCover -= offer.reportedStock;
    } else {
      break;
    }
  }

  if (quantToCover > 0) {
    return 0;
  }
  return minLead;
};

export const calcSolicitationTargetPrice = (request: {
  part: Part | PartCore;
  supply?: PartSupply;
  quant: number;
}) => {
  // check if buyable quant is more than quant
  const buyable = request.supply?.buyable || 0;
  if (buyable > request.quant) {
    return (
      calcReferenceBreakPrice(
        request.supply?.buyableReferencePrices || [],
        request.quant
      ) * 0.9
    ).toFixed(4);
  }

  return (
    calcReferenceBreakPrice(
      request.supply?.referencePrices || [],
      request.quant
    ) * 1.5
  ).toFixed(4);
};

export const calcBuyableStock = (request: {
  offers: (PartOffer | Offer)[];
  overlap: boolean;
}): number => {
  let buyableStock = 0;
  if (request.overlap) {
    for (const offer of request.offers) {
      if (
        (offer.supplier.buyable ||
          offer.offerType === OfferType.QUOTE ||
          offer.offerType === OfferType.SUPPLIER_QUOTE) &&
        offer.reportedStock > buyableStock
      ) {
        buyableStock = offer.reportedStock;
      }
    }
  } else {
    for (const offer of request.offers) {
      if (
        (offer.supplier.buyable ||
          offer.offerType === OfferType.QUOTE ||
          offer.offerType === OfferType.SUPPLIER_QUOTE) &&
        offer.reportedStock
      ) {
        buyableStock += offer.reportedStock;
      }
    }
  }
  return buyableStock;
};

export const calcOffersToUse = (request: {
  offers: (PartOffer | Offer | DecoratedOffer | OfferWithPartId)[];
  quant: number;
  addReelingPrice: number;
  useUnitOfMeasure?: boolean;
}): OffersToUse | null => {
  const sortedBids = calcSortedBids(
    request.offers,
    request.quant,
    false,
    request.useUnitOfMeasure
  );

  let quantToCover = request.quant; // this will track how much of the request quant we still need to price a source for
  let totalPrice = 0;
  let usedBids = []; // this will track the bids used to calculate the total price
  let actualPurchaseQuant = 0;
  for (const bid of sortedBids) {
    if (quantToCover > 0) {
      if (bid.bidQuant <= quantToCover) {
        quantToCover -= bid.bidQuant;
        actualPurchaseQuant += bid.bidQuant;
        totalPrice += bid.bidPrice;
        usedBids.push(bid);
      } else {
        const rebidBreak = calcBestOfferBreakPrice(
          bid.offer,
          quantToCover,
          false
        );
        const rebidQuant = rebidBreak?.purchaseQuant || 0;
        const rebidPrice = rebidBreak?.totalPrice || 0;
        totalPrice += rebidPrice;
        actualPurchaseQuant += rebidQuant;
        quantToCover = 0;
        usedBids.push(bid);
      }
    } else {
      break;
    }
  }
  if (!quantToCover) {
    // check if a single bid could have covered the required quantity at a lower price
    for (const bid of usedBids) {
      if (bid.bidQuant >= request.quant) {
        totalPrice = bid.bidPrice;
        actualPurchaseQuant = bid.bidQuant;
        usedBids = [bid];
      }
    }

    const customReelingPrice = _.some(usedBids, 'offer.customReelSku')
      ? request.addReelingPrice
      : 0;
    return {
      totalPrice: totalPrice + customReelingPrice,
      offers: _.map(usedBids, 'offer'),
      actualPurchaseQuant,
      unitPrice: (totalPrice + customReelingPrice) / actualPurchaseQuant,
      isBackordered: Boolean(_.first(_.map(usedBids, 'offer'))?.isBackordered),
    };
  }

  // fall back on using backorder offers
  const cheapestBackorderOffer = _.chain(request.offers)
    .compact()
    .filter(
      (o) =>
        (!!o.supplier.buyable ||
          o.offerType === OfferType.QUOTE ||
          o.offerType === OfferType.SUPPLIER_QUOTE) &&
        o.prices.length > 0 &&
        (o.factoryLeadDays || 0) > 0
    )
    .sortBy((o) => calcOfferBreakPrice(o.prices, request.quant))
    .first()
    .value();

  if (cheapestBackorderOffer) {
    const bestBreak = calcBestOfferBreakPrice(
      cheapestBackorderOffer,
      request.quant,
      true
    );
    if (!bestBreak) return null;
    const customReelingPrice = cheapestBackorderOffer.customReelSku
      ? request.addReelingPrice
      : 0;
    return {
      totalPrice: bestBreak.totalPrice + customReelingPrice,
      offers: [cheapestBackorderOffer],
      actualPurchaseQuant: bestBreak.purchaseQuant,
      unitPrice:
        bestBreak.unitPrice + customReelingPrice / bestBreak.purchaseQuant,
      isBackordered: true,
    };
  }
  return null;
};

const offerLead = (o: DecoratedOffer) => {
  if (
    o.offerType === OfferType.QUOTE ||
    o.offerType === OfferType.SUPPLIER_QUOTE
  ) {
    return o.lead;
  }
  return (
    o.lead -
    (o.supplier.lead || 0) +
    (o.orgSupplier?.shipsInLead || 0) +
    (_.min(
      _.compact([
        ..._.map(o.orgSupplier?.shippingOptions || [], 'shippingLead'),
        o.orgSupplier?.freeShip?.shippingLead,
      ])
    ) || 0)
  );
};

export const groupOverlappingOffers = (
  offers: DecoratedOffer[],
  useUnitOfMeasure?: boolean
) => {
  if (!offers.length) {
    return [];
  }

  const groups: DecoratedOfferGroup[] = [];

  const uniqueOrgSuppliers = _.chain(offers)
    .map('orgSupplier')
    .uniqBy('objectId')
    .compact()
    .value();

  const uniquePublicSuppliers = _.chain(offers)
    .map('publicSupplier')
    .uniqBy('objectId')
    .compact()
    .value();

  const uniqueSupplierPartHash = _.chain(offers)
    .map((o) => ({
      supplierObjectId: o.orgSupplier?.objectId ?? o.publicSupplier?.objectId,
      partId: o.part?.id,
      offerType: o.offerType,
    }))
    .uniqBy((sp) => `${sp.supplierObjectId}-${sp.partId}-${sp.offerType}`)
    .value();

  _.forEach(uniqueSupplierPartHash, (sp) => {
    const relatedOffers = _.filter(
      offers,
      (o) =>
        (o.orgSupplier?.objectId === sp.supplierObjectId ||
          o.publicSupplier?.objectId === sp.supplierObjectId) &&
        o.part?.id === sp.partId
    );

    const orgSupplier = _.find(uniqueOrgSuppliers, {
      objectId: sp.supplierObjectId,
    });

    const publicSupplier = _.find(uniquePublicSuppliers, {
      objectId: sp.supplierObjectId,
    });

    if (orgSupplier?.overlap || publicSupplier?.overlap) {
      groups.push(
        ..._.chain(relatedOffers)
          .groupBy((o) => offerLead(o))
          .values()
          .map(
            (og) =>
              ({
                orgSupplier,
                publicSupplier,
                part: _.first(og)?.part,
                offerType: _.first(og)?.offerType,
                offers: og,
                reportedStock: _.maxBy(og, (o) =>
                  useUnitOfMeasure
                    ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
                    : _.toNumber(o.reportedStock)
                )?.reportedStock,
                bulkQuant: _.maxBy(og, (o) =>
                  useUnitOfMeasure ? _.toNumber(o.bulkQuant || 1) : 1
                )?.bulkQuant,
                lead: offerLead(_.first(og) as DecoratedOffer),
                v2OrgPart: _.first(og)?.v2OrgPart,
              } as DecoratedOfferGroup)
          )
          .value()
      );
    } else {
      groups.push(
        ..._.chain(relatedOffers)
          .map(
            (o) =>
              ({
                orgSupplier,
                publicSupplier,
                part: o.part,
                offerType: o.offerType,
                offers: [o],
                reportedStock: useUnitOfMeasure
                  ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
                  : _.toNumber(o.reportedStock),
                bulkQuant: useUnitOfMeasure ? _.toNumber(o.bulkQuant || 1) : 1,
                lead: offerLead(o),
                v2OrgPart: o.v2OrgPart,
              } as DecoratedOfferGroup)
          )
          .value()
      );
    }
  });

  return groups;
};

export const filterOffersByPurchaseRule = (
  offerGroups: DecoratedOfferGroup[],
  quant: number,
  purchaseRule: PurchaseRule | null | undefined,
  preferences: OptimizePreferences | undefined,
  useUnitOfMeasure?: boolean
) => {
  let filteredOfferGroups = offerGroups;

  // filter out offers based on backorder preferences, maxLead, dropShip, and buyability
  filteredOfferGroups = _.filter(filteredOfferGroups, (o) => {
    if (
      !o.orgSupplier?.buyable &&
      !o.publicSupplier?.buyable &&
      o.offerType !== OfferType.QUOTE &&
      o.offerType !== OfferType.SUPPLIER_QUOTE
    )
      return false;

    if (
      o.orgSupplier?.connectionType ===
        OrgSupplierConnection.SUPPLIER_NETWORK &&
      o.orgSupplier?.nonBuyableMfrs?.includes(o.part?.mfg || '')
    )
      return false;

    if (
      preferences?.dropShip &&
      !o.orgSupplier?.allowDropship &&
      !o.publicSupplier?.allowDropship
    )
      return false;

    if (
      !o.orgSupplier &&
      o.publicSupplier?.nonBuyableMfrs?.includes(o.part?.mfg || '')
    )
      return false;

    if (!o.reportedStock && !_.first(o.offers)?.factoryLeadDays) return false;

    if (
      preferences?.noBackorders &&
      (_.first(o.offers)?.isBackordered || !o.reportedStock)
    )
      return false;

    if (preferences?.maxLead && o.lead > preferences.maxLead) return false;

    return true;
  });

  // filter out offers based on allow and banned suppliers in purchase rule
  if (purchaseRule) {
    filteredOfferGroups = _.filter(filteredOfferGroups, (o) => {
      if (
        preferences?.quotesOverridePurchaseRule &&
        (o.offerType === OfferType.QUOTE ||
          o.offerType === OfferType.SUPPLIER_QUOTE)
      )
        return true;

      if (!_.first(o.offers)?.authorized && purchaseRule.authorizedOnly)
        return false;

      // offers from preferred suppliers are always allowed
      if (
        _.chain(purchaseRule.preferredOrgSuppliers || [])
          .map('objectId')
          .includes(o.orgSupplier?.objectId || '')
          .value()
      )
        return true;

      // if allowed suppliers are defined, only allow offers from those suppliers
      if (
        purchaseRule.allowedOrgSuppliers &&
        purchaseRule.allowedOrgSuppliers.length > 0 &&
        !_.chain(purchaseRule.allowedOrgSuppliers)
          .map('objectId')
          .includes(o.orgSupplier?.objectId || '')
          .value()
      )
        return false;

      // banned suppliers are never allowed
      if (
        _.chain(purchaseRule.bannedOrgSuppliers || [])
          .map('objectId')
          .includes(o.orgSupplier?.objectId || '')
          .value()
      )
        return false;

      return true;
    });
  }

  // filter out backordered offers based on avoidBackorders preference
  if (preferences?.avoidBackorders) {
    const nonBackorderStock = _.chain(filteredOfferGroups)
      .filter((o) => !_.first(o.offers)?.isBackordered)
      .sumBy((o) =>
        useUnitOfMeasure
          ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
          : _.toNumber(o.reportedStock)
      )
      .value();
    if (nonBackorderStock > quant) {
      filteredOfferGroups = _.filter(
        filteredOfferGroups,
        (o) =>
          !_.first(o.offers)?.isBackordered &&
          (useUnitOfMeasure
            ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
            : _.toNumber(o.reportedStock)) > 0
      );
    }
  }

  // filter out offers based on targetLead preference
  if (preferences?.targetLead && preferences.targetLead > 0) {
    const fastEnoughStock = _.chain(filteredOfferGroups)
      .filter((o) => o.lead <= (preferences.targetLead || 0))
      .sumBy((o) =>
        useUnitOfMeasure
          ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
          : _.toNumber(o.reportedStock)
      )
      .value();
    if (fastEnoughStock > quant) {
      filteredOfferGroups = _.filter(
        filteredOfferGroups,
        (o) => o.lead <= (preferences.targetLead || 0)
      );
    }
  }

  // filter out offers based on preferred/last-resort purchase rule preferences
  if (purchaseRule) {
    const preferredStock = _.chain(filteredOfferGroups)
      .filter(
        (og) =>
          _.chain(purchaseRule.preferredOrgSuppliers || [])
            .map('objectId')
            .includes(og.orgSupplier?.objectId || '')
            .value() && !_.some(og.offers, (o) => !!o.isBackordered)
      )
      .sumBy((o) =>
        useUnitOfMeasure
          ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
          : _.toNumber(o.reportedStock)
      )
      .value();
    if (preferredStock > quant) {
      filteredOfferGroups = _.filter(filteredOfferGroups, (o) =>
        _.chain(purchaseRule.preferredOrgSuppliers || [])
          .map('objectId')
          .includes(o.orgSupplier?.objectId || '')
          .value()
      );
    }

    const nonLastResortStock = _.chain(filteredOfferGroups)
      .filter(
        (o) =>
          !_.chain(purchaseRule.lastResortOrgSuppliers || [])
            .map('objectId')
            .includes(o.orgSupplier?.objectId || '')
            .value()
      )
      .sumBy((o) =>
        useUnitOfMeasure
          ? _.toNumber(o.reportedStock) * _.toNumber(o.bulkQuant || 1)
          : _.toNumber(o.reportedStock)
      )
      .value();

    if (nonLastResortStock > quant) {
      filteredOfferGroups = _.filter(
        filteredOfferGroups,
        (o) =>
          !_.chain(purchaseRule.lastResortOrgSuppliers || [])
            .map('objectId')
            .includes(o.orgSupplier?.objectId || '')
            .value()
      );
    }
  }

  return filteredOfferGroups;
};

export const checkIfFilteredByPurchaseRule = (
  offerGroups: DecoratedOfferGroup[],
  quant: number,
  purchaseRule: PurchaseRule | null | undefined,
  preferences: OptimizePreferences | undefined,
  useUnitOfMeasure?: boolean
): boolean => {
  const filtered = filterOffersByPurchaseRule(
    offerGroups,
    quant,
    purchaseRule,
    preferences,
    useUnitOfMeasure
  );
  return filtered.length > 0;
};

export const calcSupplierFastestTransitTime = (
  supplier: OrgSupplier | PublicSupplier | undefined
) =>
  _.chain(supplier?.shippingOptions)
    .map((s) => _.toNumber(s.shippingLead))
    .min()
    .value() || 0;

const calcSortedBidsFromGroups = (
  offerGroups: DecoratedOfferGroup[],
  quant: number,
  addReeling: boolean,
  useUnitOfMeasure?: boolean
) =>
  _.chain(offerGroups)
    .map((og) => {
      if (og.offers.length < 2) {
        return calcSortedBids(og.offers, quant, addReeling, useUnitOfMeasure);
      }

      const sortedBids = calcSortedBids(
        og.offers,
        quant,
        false,
        useUnitOfMeasure
      );
      let quantToCover = Math.min(
        quant,
        useUnitOfMeasure
          ? _.toNumber(og.reportedStock) * _.toNumber(og.bulkQuant || 1)
          : _.toNumber(og.reportedStock)
      );
      let combinedPrice = 0;
      let combinedQuant = 0;
      let usedBids = [];
      for (const bid of sortedBids) {
        if (quantToCover > 0) {
          if (bid.bidQuant <= quantToCover) {
            quantToCover -= bid.bidQuant;
            combinedQuant += bid.bidQuant;
            combinedPrice += _.toNumber(bid.bidPrice);
            usedBids.push(bid);
          } else {
            const rebidBreak = calcBestOfferBreakPrice(
              bid.offer,
              quantToCover,
              false,
              useUnitOfMeasure
            );
            const rebidQuant = rebidBreak?.purchaseQuant || 0;
            const rebidPrice = _.toNumber(rebidBreak?.totalPrice || 0);
            combinedPrice += _.toNumber(rebidPrice);
            combinedQuant += rebidQuant;
            quantToCover = 0;
            usedBids.push(bid);
          }
        } else {
          break;
        }
      }
      if (!quantToCover) {
        // check if a single offer could have covered the required quantity at a lower price
        for (const bid of usedBids) {
          if (bid.bidQuant >= quant) {
            combinedPrice = _.toNumber(bid.bidPrice);
            combinedQuant = bid.bidQuant;
            usedBids = [bid];
          }
        }
      }

      const addReelingPrice =
        addReeling && _.first(og.offers)?.customReelSku
          ? _.toNumber(og.orgSupplier?.customReelPrice || 0)
          : 0;

      return {
        bidQuant: combinedQuant,
        bidPrice: _.toNumber(combinedPrice) + addReelingPrice,
        unitPrice:
          (_.toNumber(combinedPrice) + addReelingPrice) / combinedQuant,
        offer: _.first(usedBids)?.offer as DecoratedOffer,
      };
    })
    .flatten()
    .sortBy('unitPrice')
    .value();

export const calcOffersToUseFromGroups = (request: {
  offerGroups: DecoratedOfferGroup[];
  quant: number;
  addReeling: boolean;
  useUnitOfMeasure?: boolean;
}): Omit<OfferLinesToUse, 'id'> | null => {
  const sortedBids = calcSortedBidsFromGroups(
    request.offerGroups,
    request.quant,
    request.addReeling,
    request.useUnitOfMeasure
  );

  let quantToCover = request.quant; // this will track how much of the request quant we still need to price a source for
  let totalPrice = 0;
  let usedBids = []; // this will track the bids used to calculate the total price
  let actualPurchaseQuant = 0;
  for (const bid of sortedBids) {
    if (quantToCover > 0) {
      if (bid.bidQuant <= quantToCover) {
        quantToCover -= bid.bidQuant;
        actualPurchaseQuant += bid.bidQuant;
        totalPrice += bid.bidPrice;
        usedBids.push(bid);
      } else {
        const rebidBreak = calcBestOfferBreakPrice(
          bid.offer,
          quantToCover,
          false,
          request.useUnitOfMeasure
        );
        const rebidQuant = rebidBreak?.purchaseQuant || 0;
        const rebidPrice = rebidBreak?.totalPrice || 0;
        totalPrice += rebidPrice;
        actualPurchaseQuant += rebidQuant;
        quantToCover = 0;
        usedBids.push(bid);
      }
    } else {
      break;
    }
  }
  if (!quantToCover) {
    // check if a single bid could have covered the required quantity at a lower price
    for (const bid of usedBids) {
      if (bid.bidQuant >= request.quant) {
        totalPrice = bid.bidPrice;
        actualPurchaseQuant = bid.bidQuant;
        usedBids = [bid];
      }
    }

    return {
      totalPrice,
      offers: _.map(usedBids, 'offer') as DecoratedOffer[],
      actualPurchaseQuant,
      unitPrice: totalPrice / actualPurchaseQuant,
      isBackordered: Boolean(_.first(_.map(usedBids, 'offer'))?.isBackordered),
    };
  }

  // fall back on using backorder offers
  const cheapestBackorderOffer = _.chain(request.offerGroups)
    .map('offers')
    .flatten()
    .compact()
    .filter(
      (o) =>
        (!!o.supplier.buyable ||
          o.offerType === OfferType.QUOTE ||
          o.offerType === OfferType.SUPPLIER_QUOTE) &&
        o.prices.length > 0 &&
        (o.factoryLeadDays || 0) > 0
    )
    .sortBy((o) => calcOfferBreakPrice(o.prices, request.quant))
    .first()
    .value();

  if (cheapestBackorderOffer) {
    const bestBreak = calcBestOfferBreakPrice(
      cheapestBackorderOffer,
      request.quant,
      true
    );
    if (!bestBreak) return null;

    return {
      totalPrice: bestBreak.totalPrice,
      offers: [cheapestBackorderOffer],
      actualPurchaseQuant: bestBreak.purchaseQuant,
      unitPrice: bestBreak.unitPrice / bestBreak.purchaseQuant,
      isBackordered: true,
    };
  }
  return null;
};

export const calcFactoryLead = (request: { offers: (PartOffer | Offer)[] }) =>
  _.chain(request.offers).map('factoryLeadDays').min().value() || 0;

const normalizeForTariffMatch = (s: string | null): string =>
  s?.replace(/\./g, '') ?? '';

const matchTariffRate = (
  request: {
    coo: string | null;
    importCountry: string | null;
    htsus: string | null;
  },
  tariffRates: TariffRate[]
): TariffRate | null => {
  const normRequestCoo = normalizeForTariffMatch(request.coo);
  const normRequestImportCountry = normalizeForTariffMatch(
    request.importCountry
  );
  const normRequestHtsus = normalizeForTariffMatch(request.htsus);

  let countryMatchedRates: TariffRate[] = [];
  if (!!request.coo && !!request.importCountry) {
    countryMatchedRates = tariffRates.filter(
      (t) =>
        (t.coo?.some((c) => normalizeForTariffMatch(c) === normRequestCoo) ??
          false) &&
        normalizeForTariffMatch(t.importCountry) === normRequestImportCountry
    );
  }
  if (countryMatchedRates.length === 0) {
    countryMatchedRates = tariffRates.filter(
      (t) =>
        t.coo === null &&
        normalizeForTariffMatch(t.importCountry) === normRequestImportCountry
    );
  }
  if (countryMatchedRates.length === 0) {
    return null;
  }

  let htsusMatchedRates: TariffRate[] = [];
  if (request.htsus) {
    htsusMatchedRates = countryMatchedRates.filter((t) =>
      _.some(t.htsusList, (htsus) =>
        normRequestHtsus.startsWith(normalizeForTariffMatch(htsus))
      )
    );
  }
  if (htsusMatchedRates.length === 0) {
    htsusMatchedRates = countryMatchedRates.filter(
      (t) =>
        t.htsusList == null ||
        (Array.isArray(t.htsusList) && t.htsusList.length === 0)
    );
  }
  if (htsusMatchedRates.length > 0) {
    const overlapLength = (rate: TariffRate): number => {
      if (!rate.htsusList?.length) return 0;
      const lengths = rate.htsusList.map((htsus) => {
        const norm = normalizeForTariffMatch(htsus);
        return normRequestHtsus.startsWith(norm) ? norm.length : 0;
      });
      return _.max(lengths) ?? 0;
    };
    return _.maxBy(htsusMatchedRates, overlapLength) ?? htsusMatchedRates[0];
  }
  return null;
};

const calcBidLandedCosts = (request: {
  bidPrice: number;
  offer: DecoratedOffer;
  priceModel: PriceModel | null;
  shipToLocation: StockLocationLite | null | undefined;
  isManagedByCofactr: boolean;
  tariffRates: TariffRate[];
}) => {
  const landedCosts: LandedCostLine[] = [];
  let landedPrice = request.bidPrice;

  const importCountry = request.shipToLocation?.addressCountry || 'US';
  const shipsFromCountry =
    request.offer.country ??
    request.offer.orgSupplier?.shipsFromCountries?.[0] ??
    request.offer.publicSupplier?.shipsFromCountries?.[0] ??
    'US';
  const coo = request.offer.part?.coo || request.offer.country || 'US';
  const htsus = request.offer.part?.htsus || null;
  const supplierTariffAdjustment =
    request.offer.orgSupplier?.supplierTariffAdjustment ??
    request.offer.publicSupplier?.supplierTariffAdjustment ??
    0;

  if (importCountry !== shipsFromCountry) {
    const tariffRate = matchTariffRate(
      { coo, importCountry, htsus },
      request.tariffRates
    );
    if (tariffRate?.tariffRate) {
      landedCosts.push({
        price: request.bidPrice * tariffRate.tariffRate,
        description: `${landedCostFeeDescriptions.tariff} (${(
          tariffRate.tariffRate * 100
        ).toLocaleString()}%)`,
        type: CofactrPurchaseChargeType.TARIFF,
      });
      landedPrice += request.bidPrice * tariffRate.tariffRate;
    }
  }

  // handle supplier tariff surcharges
  if (
    importCountry === shipsFromCountry &&
    supplierTariffAdjustment > 0 &&
    !request.offer.duties
  ) {
    const tariffRate = matchTariffRate(
      { coo, importCountry, htsus },
      request.tariffRates
    );
    if (tariffRate?.tariffRate) {
      landedCosts.push({
        price:
          request.bidPrice * tariffRate.tariffRate * supplierTariffAdjustment,
        description: `${landedCostFeeDescriptions.supplierTariffAdjustment} (${(
          tariffRate.tariffRate *
          100 *
          supplierTariffAdjustment
        ).toLocaleString()}%)`,
        type: CofactrPurchaseChargeType.SUPPLIER_TRANSACTION,
      });
      landedPrice +=
        request.bidPrice * tariffRate.tariffRate * supplierTariffAdjustment;
    }
  }

  if (request.offer.duties) {
    landedCosts.push({
      price: _.toNumber(request.offer.duties),
      description: landedCostFeeDescriptions.supplierTariffAdjustment,
      type: CofactrPurchaseChargeType.SUPPLIER_TRANSACTION,
    });
    landedPrice += _.toNumber(request.offer.duties);
  }

  // handle Managed by Cofactr procurement fees
  if (request.priceModel?.buyMarkupPercent && request.isManagedByCofactr) {
    const buyMarkupPercentDiscount = _.toNumber(
      request.offer.orgSupplier?.buyMarkupPercentDiscount ??
        request.offer.publicSupplier?.buyMarkupPercentDiscount ??
        0
    );
    const markupPercent = request.priceModel?.ignoreBuyMarkupPercentDiscount
      ? _.toNumber(request.priceModel?.buyMarkupPercent ?? 0)
      : _.toNumber(request.priceModel?.buyMarkupPercent ?? 0) -
        buyMarkupPercentDiscount;

    landedCosts.push({
      price: request.bidPrice * markupPercent,
      description: landedCostFeeDescriptions.managedPurchaseFee,
      type: CofactrPurchaseChargeType.SOURCING,
      excludeFromResult: true, // we will recalculate this in the supplier landed costs
    });
    landedPrice += request.bidPrice * markupPercent;
  }
  return {
    landedPrice,
    landedCosts,
  };
};

export const calcSupplierLandedCosts = (request: {
  supplier: Supplier;
  selectedBids: OptimizerBid[];
  leadTime: number;
  priceModel: PriceModel | null;
}) => {
  const landedCosts: LandedCostLine[] = [];
  const totalLineCosts = _.sumBy(
    request.selectedBids,
    (bid) => bid.bidTotalPrice
  );
  const totalLineLandedCosts = _.sumBy(
    request.selectedBids,
    (bid) => bid.bidTotalLandedPrice
  );
  let landedPrice = totalLineLandedCosts;

  // check if the free shipping speed is fast enough for this lead time
  const fastestShipSpeed = calcSupplierFastestTransitTime(request.supplier);
  const slowestSelectedBid =
    _.chain(request.selectedBids)
      .map((bid) => bid.minLead)
      .max()
      .value() - fastestShipSpeed;

  const freeShipIsFastEnough =
    slowestSelectedBid + (request.supplier.freeShip?.shippingLead ?? 0) <=
    request.leadTime;

  let freeShipEligible = false;
  if (
    request.supplier.freeShip?.threshold !== null &&
    request.supplier.freeShip?.threshold !== undefined
  ) {
    if (totalLineCosts >= request.supplier.freeShip?.threshold) {
      freeShipEligible = true;
    }
  }

  // add free shipping to landed costs if eligible, otherwise add shipping cost
  if (freeShipIsFastEnough && freeShipEligible) {
    landedCosts.push({
      price: 0,
      description: landedCostFeeDescriptions.supplierFreeShipping,
      type: CofactrPurchaseChargeType.SHIPPING,
    });
  } else {
    // calculate the correct shipping speed to use
    const slowestApplicableShippingSpeed =
      request.leadTime - slowestSelectedBid;
    const matchingShippingOption = _.chain(request.supplier.shippingOptions)
      .filter((option) => option.shippingLead <= slowestApplicableShippingSpeed)
      .orderBy('price', 'asc')
      .first()
      .value();

    if (matchingShippingOption) {
      landedPrice += _.toNumber(matchingShippingOption?.price);
      landedCosts.push({
        price: _.toNumber(matchingShippingOption?.price),
        description: landedCostFeeDescriptions.supplierShipping,
        type: CofactrPurchaseChargeType.SHIPPING,
      });
    }
  }

  // calculate Cofactr payment processing fees
  const isNonBuyableSupplier =
    !request.supplier.buyable &&
    _.some(request.selectedBids, (bid) => bid.isManagedByCofactr);
  if (isNonBuyableSupplier) {
    if (totalLineLandedCosts > WIRE_FEE_THRESHOLD) {
      landedPrice += WIRE_FEE;
      landedCosts.push({
        price: WIRE_FEE,
        description: landedCostFeeDescriptions.supplierWireTransfer,
        type: CofactrPurchaseChargeType.PAYMENT,
      });
    } else {
      landedPrice += totalLineLandedCosts * PAYMENT_PROCESSING_FEE;
      landedCosts.push({
        price: totalLineLandedCosts * PAYMENT_PROCESSING_FEE,
        description: landedCostFeeDescriptions.supplierPaymentProcessing,
        type: CofactrPurchaseChargeType.PAYMENT,
      });
    }
  }

  // calculate tariffs and customs clearance fees
  const tariffTotal = _.chain(request.selectedBids)
    .map((bid) => bid.landedCosts)
    .flatten()
    .filter((cost) => cost.type === CofactrPurchaseChargeType.TARIFF)
    .sumBy('price')
    .value();

  const hasDeminimisExcludedCountries = _.chain(request.selectedBids)
    .map((bid) => {
      const coo = bid.offer.part?.coo || bid.offer.country || 'US';
      const bidTariffTotal = _.chain(bid.landedCosts)
        .filter((cost) => cost.type === CofactrPurchaseChargeType.TARIFF)
        .sumBy('price')
        .value();
      return bidTariffTotal > 0 && DEMINIMIS_EXCLUDED_COUNTRIES.includes(coo);
    })
    .some()
    .value();

  if (
    tariffTotal > 0 &&
    !hasDeminimisExcludedCountries &&
    tariffTotal < DEMINIMIS_THRESHOLD
  ) {
    landedPrice += _.toNumber(
      request.priceModel?.customsBrokerageDeminimis ?? 0
    );
    landedCosts.push({
      price: _.toNumber(request.priceModel?.customsBrokerageDeminimis ?? 0),
      description: landedCostFeeDescriptions.customsBrokerageDeminimis,
      type: CofactrPurchaseChargeType.CUSTOMS_BROKERAGE,
    });
  } else if (tariffTotal > 0) {
    landedPrice += _.toNumber(
      request.priceModel?.customsBrokerageStandard ?? 0
    );
    landedCosts.push({
      price: _.toNumber(request.priceModel?.customsBrokerageStandard ?? 0),
      description: landedCostFeeDescriptions.customsBrokerageStandard,
      type: CofactrPurchaseChargeType.CUSTOMS_BROKERAGE,
    });
  }

  // handle Managed by Cofactr procurement fees
  const buyMarkupPercentDiscount = _.toNumber(
    request.supplier?.buyMarkupPercentDiscount ?? 0
  );
  const markupPercent = request.priceModel?.ignoreBuyMarkupPercentDiscount
    ? _.toNumber(request.priceModel?.buyMarkupPercent ?? 0)
    : _.toNumber(request.priceModel?.buyMarkupPercent ?? 0) -
      buyMarkupPercentDiscount;
  const isManagedByCofactr = _.some(
    request.selectedBids,
    (bid) => bid.isManagedByCofactr
  );
  if (markupPercent > 0 && isManagedByCofactr) {
    landedCosts.push({
      price: landedPrice * markupPercent,
      description: landedCostFeeDescriptions.managedPurchaseFee,
      type: CofactrPurchaseChargeType.SOURCING,
    });
    landedPrice += landedPrice * markupPercent;
  }

  return {
    landedPrice,
    landedCosts,
  };
};

const optimizerBidsFromOfferGroup: (
  lineId: string,
  offerGroup: DecoratedOfferGroup,
  quant: number,
  reelingRequired: boolean,
  noBackorder: boolean,
  priceModel: PriceModel | null,
  shipToLocation: StockLocationLite | null | undefined,
  isManagedByCofactr: boolean,
  tariffRates: TariffRate[]
) => OptimizerBid[] = (
  lineId,
  offerGroup,
  quant,
  reelingRequired,
  noBackorder,
  priceModel,
  shipToLocation,
  isManagedByCofactr,
  tariffRates
) => {
  let selectedBid = null;
  let backorderBid = null;

  if (offerGroup.offers.length < 2) {
    selectedBid = _.first(
      calcSortedBids(offerGroup.offers, quant, false, true)
    ) as BaseBid;
  } else {
    const sortedBids = calcSortedBids(offerGroup.offers, quant, false, true);
    let quantToCover = Math.min(quant, offerGroup.reportedStock);
    let combinedPrice = 0;
    let combinedQuant = 0;
    let usedBids = [];
    for (const bid of sortedBids) {
      if (quantToCover > 0) {
        if (bid.bidQuant <= quantToCover) {
          quantToCover -= bid.bidQuant;
          combinedQuant += bid.bidQuant;
          combinedPrice += _.toNumber(bid.bidPrice);
          usedBids.push(bid);
        } else {
          const rebidBreak = calcBestOfferBreakPrice(
            bid.offer,
            quantToCover,
            false,
            true
          );
          const rebidQuant = rebidBreak?.purchaseQuant || 0;
          const rebidPrice = _.toNumber(rebidBreak?.totalPrice || 0);
          combinedPrice += _.toNumber(rebidPrice);
          combinedQuant += rebidQuant;
          quantToCover = 0;
          usedBids.push(bid);
        }
      } else {
        break;
      }
    }
    if (!quantToCover) {
      // check if a single offer could have covered the required quantity at a lower price
      for (const bid of usedBids) {
        if (bid.bidQuant >= quant) {
          combinedPrice = _.toNumber(bid.bidPrice);
          combinedQuant = bid.bidQuant;
          usedBids = [bid];
        }
      }
    }

    selectedBid = {
      bidQuant: combinedQuant,
      bidPrice: _.toNumber(combinedPrice),
      unitPrice: _.toNumber(combinedPrice) / combinedQuant,
      offer: _.first(usedBids)?.offer as DecoratedOffer,
    } as BaseBid;
  }

  // generate backorder bid
  if ((!selectedBid || selectedBid.bidQuant < quant) && !noBackorder) {
    const cheapestBackorderOffer = _.chain(offerGroup.offers)
      .compact()
      .filter(
        (o) =>
          (o.supplier.buyable ||
            o.offerType === OfferType.QUOTE ||
            o.offerType === OfferType.SUPPLIER_QUOTE) &&
          o.prices.length > 0 &&
          o.factoryLeadDays > 0
      )
      .sortBy(
        (o) =>
          calcOfferBreakPrice(o.prices, quant, o.bulkQuant || 1) *
          calcQuantWithMoqAndMultiple(quant, o.moq || 1, o.multiple || 1)
      )
      .first()
      .value();

    if (cheapestBackorderOffer) {
      const bestBreak = calcBestOfferBreakPrice(
        cheapestBackorderOffer,
        quant,
        true,
        true
      );
      if (bestBreak) {
        backorderBid = {
          bidQuant: bestBreak.purchaseQuant,
          bidPrice: bestBreak.totalPrice,
          unitPrice: bestBreak.unitPrice,
          offer: cheapestBackorderOffer,
        } as BaseBid;
      }
    }
  }

  if (
    (!selectedBid || !selectedBid.offer) &&
    (!backorderBid || !backorderBid.offer)
  )
    return [];

  const bids: OptimizerBid[] = [];
  if (selectedBid && selectedBid.offer) {
    const { landedPrice, landedCosts } = calcBidLandedCosts({
      bidPrice: _.toNumber(selectedBid.bidPrice),
      offer: selectedBid.offer,
      priceModel,
      shipToLocation,
      isManagedByCofactr,
      tariffRates,
    });

    const useCustomReeling =
      reelingRequired &&
      selectedBid.offer.customReelSku &&
      selectedBid.offer.orgSupplier?.useCustomReeling;
    const addReelingPrice = useCustomReeling
      ? _.toNumber(selectedBid.offer.orgSupplier?.customReelPrice || 0)
      : 0;

    const shipsInLeadOffset = selectedBid.offer.extraLeadDays || 0;

    const shipsInLead =
      shipsInLeadOffset +
      (selectedBid.offer.orgSupplier?.shipsInLead ??
        selectedBid.offer.publicSupplier?.shipsInLead ??
        0);

    bids.push({
      lineId,
      partId: selectedBid.offer.part?.id,
      v2OrgPartId: selectedBid.offer.v2OrgPartId,
      bidQuant: _.toNumber(selectedBid.bidQuant),
      bidTotalPrice: _.toNumber(selectedBid.bidPrice),
      bidUnitPrice: _.toNumber(selectedBid.unitPrice),
      bidTotalLandedPrice: _.toNumber(landedPrice + addReelingPrice),
      bidUnitLandedPrice:
        (landedPrice + addReelingPrice) / _.toNumber(selectedBid.bidQuant),
      offer: selectedBid.offer,
      minLead:
        shipsInLead +
        calcSupplierFastestTransitTime(
          selectedBid.offer.orgSupplier ?? selectedBid.offer.publicSupplier
        ),
      shipsInLead,
      isReeled:
        useCustomReeling ||
        _.includes(
          _.toLower(selectedBid.offer.package || ''),
          PackagingOptions.REEL
        ),
      isBackorder: selectedBid.offer.isBackordered,
      isManagedByCofactr,
      landedCosts: addReelingPrice
        ? [
            ...landedCosts,
            {
              price: addReelingPrice,
              description: 'Supplier Custom Reeling',
              type: CofactrPurchaseChargeType.OTHER,
            },
          ]
        : landedCosts,
    } as OptimizerBid);
  }

  if (backorderBid && backorderBid.offer) {
    const { landedPrice, landedCosts } = calcBidLandedCosts({
      bidPrice: _.toNumber(backorderBid.bidPrice),
      offer: backorderBid.offer,
      priceModel,
      shipToLocation,
      isManagedByCofactr,
      tariffRates,
    });

    const useCustomReeling =
      reelingRequired &&
      backorderBid.offer.customReelSku &&
      backorderBid.offer.orgSupplier?.useCustomReeling;
    const addReelingPrice = useCustomReeling
      ? _.toNumber(backorderBid.offer.orgSupplier?.customReelPrice || 0)
      : 0;

    const shipsInLeadOffset = backorderBid.offer.factoryLeadDays || 0;

    const shipsInLead =
      shipsInLeadOffset +
      (backorderBid.offer.orgSupplier?.shipsInLead ??
        backorderBid.offer.publicSupplier?.shipsInLead ??
        0);

    bids.push({
      lineId,
      partId: backorderBid.offer.part?.id,
      v2OrgPartId: backorderBid.offer.v2OrgPartId,
      bidQuant: _.toNumber(backorderBid.bidQuant),
      bidTotalPrice: _.toNumber(backorderBid.bidPrice),
      bidUnitPrice: _.toNumber(backorderBid.unitPrice),
      bidTotalLandedPrice: _.toNumber(landedPrice + addReelingPrice),
      bidUnitLandedPrice:
        (landedPrice + addReelingPrice) / _.toNumber(backorderBid.bidQuant),
      offer: backorderBid.offer,
      minLead:
        shipsInLead +
        calcSupplierFastestTransitTime(
          backorderBid.offer.orgSupplier ?? backorderBid.offer.publicSupplier
        ),
      shipsInLead,
      isReeled:
        useCustomReeling ||
        _.includes(
          _.toLower(backorderBid.offer.package || ''),
          PackagingOptions.REEL
        ),
      isBackorder: true,
      isManagedByCofactr,
      landedCosts: addReelingPrice
        ? [
            ...landedCosts,
            {
              price: addReelingPrice,
              description: 'Supplier Custom Reeling',
              type: CofactrPurchaseChargeType.OTHER,
            },
          ]
        : landedCosts,
    } as OptimizerBid);
  }

  return bids;
};

export const calcOptimizerBids: (request: {
  lineOffers: FilteredLineOffers[];
  priceModel: PriceModel | null;
  tariffRates: TariffRate[];
}) => OptimizerLineBids[] = (request) =>
  _.map(request.lineOffers, (l) => {
    const reelingRequired =
      l.preferences?.destinationLocation?.requiresReels ?? false;
    const noBackorder = l.preferences?.noBackorders ?? false;

    return {
      id: l.id,
      quant: l.quant,
      bids: _.chain(l.offerGroups)
        .flatMap((og) => {
          const isDirectConnection =
            og.orgSupplier?.connectionType === OrgSupplierConnection.DIRECT;
          return optimizerBidsFromOfferGroup(
            l.id,
            og,
            l.quant,
            reelingRequired,
            noBackorder,
            request.priceModel,
            l.preferences?.destinationLocation,
            l.preferences?.managedByCofactr ===
              ManagedByCofactrPreference.FROM_SUPPLIER ||
              !l.preferences?.managedByCofactr
              ? !isDirectConnection
              : l.preferences?.managedByCofactr ===
                  ManagedByCofactrPreference.TRUE,
            request.tariffRates
          );
        })
        .compact()
        .value(),
    };
  });

export const calcValidLeadTimes: (
  lineBids: OptimizerLineBids[],
  daysToWeeksCutoff: number
) => number[] = (lineBids, daysToWeeksCutoff) => {
  const allLeadTimes = _.chain(lineBids)
    .map('bids')
    .flatten()
    .map((b) => {
      const bidLeads: number[] = [];

      const freeShipSpeed =
        (b.offer.orgSupplier || b.offer.publicSupplier)?.freeShip
          ?.shippingLead || 0;
      if (freeShipSpeed > 0) {
        bidLeads.push(freeShipSpeed + b.shipsInLead);
      }
      _.forEach(
        (b.offer.orgSupplier || b.offer.publicSupplier)?.shippingOptions,
        (s) => {
          if (s.shippingLead > 0) {
            bidLeads.push(s.shippingLead + b.shipsInLead);
          }
        }
      );
      if (
        bidLeads.length === 0 &&
        b.minLead > 0 &&
        (b.offer.offerType === OfferType.QUOTE ||
          b.offer.offerType === OfferType.SUPPLIER_QUOTE)
      ) {
        bidLeads.push(b.minLead);
      }
      return bidLeads;
    })
    .flatten()
    .uniq()
    .compact()
    .value();
  // for any lead time in allLeadTimes that is greater than days to week cutoff, round up to the nearest multiple of 5
  const weekRounded = _.chain(allLeadTimes)
    .filter((lt) => lt > daysToWeeksCutoff)
    .map((lt) => Math.ceil(lt / 5) * 5)
    .uniq()
    .value();
  const validLeadTimes = [
    ..._.filter(allLeadTimes, (lt) => lt <= daysToWeeksCutoff),
    ...weekRounded,
    0,
  ]; // we include 0 to capture any bids with unspecified lead times
  return _.sortBy(validLeadTimes);
};

export const filterOptimizerResults = (
  request: OptimizeOffersRequest,
  results: (OptimizerResult | null)[],
  optimizableLineCount?: number
) => {
  // filter out results that don't have enough buyable lines
  const minBuyablePercentage =
    request.minBuyablePercentage ?? DEFAULT_MIN_BUYABLE_PERCENTAGE;

  const compactResults = _.chain(results)
    .compact()
    .filter((r) => r?.includedLines?.length > 0)
    .value();
  const buyableFilteredResults = _.chain(compactResults)
    .filter((r) => {
      const includedLineCount = r.includedLines.length || 0;
      const excludedLineCount = r.excludedLines?.length || 0;
      const totalLineCount = includedLineCount + excludedLineCount;
      const denominator =
        optimizableLineCount != null && optimizableLineCount > 0
          ? optimizableLineCount
          : totalLineCount;
      const buyablePercent =
        denominator > 0 ? includedLineCount / denominator : 0;
      if (totalLineCount === 0) return false;
      return buyablePercent >= minBuyablePercentage;
    })
    .orderBy('leadTime', 'asc')
    .value();

  if (!buyableFilteredResults.length && compactResults.length > 0) {
    const allHaveZeroIncluded = compactResults.every(
      (r) => (r.includedLines?.length ?? 0) === 0
    );
    if (allHaveZeroIncluded) {
      const fallback = _.minBy(compactResults, 'leadTime');
      if (fallback) {
        return [fallback];
      }
    }
  }

  if (!buyableFilteredResults.length) return [];

  // filter out results that aren't sufficiently better than the next best result
  const betterThanNextBestPercentageThreshold =
    request.betterThanNextBestPercentageThreshold ??
    DEFAULT_BETTER_THAN_NEXT_BEST_PERCENTAGE_THRESHOLD;
  const betterThanNextBestDollarsThreshold =
    request.betterThanNextBestDollarsThreshold ??
    DEFAULT_BETTER_THAN_NEXT_BEST_DOLLARS_THRESHOLD;

  const betterThanNextBestFilteredResults = [_.first(buyableFilteredResults)];
  _.forEach(buyableFilteredResults, (r) => {
    const previousResult = _.last(
      betterThanNextBestFilteredResults
    ) as OptimizerResult;

    // check if this result has more included lines than the previous result since we always keep results that have more included lines
    if (r.includedLines.length > previousResult.includedLines.length) {
      betterThanNextBestFilteredResults.push(r);
    } else {
      const landedCostsDelta =
        previousResult.totalLandedCosts - r.totalLandedCosts;
      if (landedCostsDelta > betterThanNextBestDollarsThreshold) {
        betterThanNextBestFilteredResults.push(r);
      } else if (
        landedCostsDelta / previousResult.totalLandedCosts >
        betterThanNextBestPercentageThreshold
      ) {
        betterThanNextBestFilteredResults.push(r);
      }
    }
  });

  return betterThanNextBestFilteredResults;
};
